import { captureMessage } from '@tink/core/src/sentry';
import { ProductType } from '@tink/core/src/types';
import { LocaleCode, MarketCode } from '@tink/config/src/markets';
import { RefreshableItem } from '../models/refreshableItem';
import { FinancialServicesSegment } from '../models/provider';
import { ProductsType } from '../products/models/products';
import { AccountDialogType, ReportType } from '../reports/models/report';

export type AppParameters = {
  isMobileSDK: boolean;
  isIframe: boolean;
  clientId?: string;
  redirectUri?: string;
  scope?: string;
  state?: string;
  providerName?: string;
  username?: string;
  authorizationCode?: string;
  credentialsId?: string;
  productType: ProductType;
  authorizationToken?: string;
  paymentRequestId?: string;
  transferId?: string;
  consentId?: string;
  credentialsIdFromTPP?: string;
  expensesReportReference?: string;
  handoffSessionId?: string;
  trackingId: string;
  authenticate: boolean;
  debug?: boolean;
  appUri?: string;
  externalReference?: string;
  engineVersion?: string;
  refreshableItems: RefreshableItem[];
  financialInstitutionId?: string;
  financialServicesSegments?: FinancialServicesSegment[];
  customerReferenceId?: string;
  sessionId?: string;
  // Reports product (bundled flow):
  reportTypes?: ReportType[];
  accountDialogType?: AccountDialogType;
  // Used in 'products' flow
  products?: ProductsType[];
  autoRedirectMobile?: boolean;
  async?: boolean;
  locale: LocaleCode;
  market: MarketCode;
  callbackUri?: string;
  // Enable opt-in /credentials/refresh
  optIn?: boolean;
  connectedProviders?: string;
};

export class ApplicationParameters {
  private logError: typeof captureMessage;

  public urlParams: AppParameters = {} as AppParameters;

  public initialized = false;

  constructor(logError: typeof captureMessage = captureMessage) {
    this.logError = logError;
  }

  init(urlParams: AppParameters) {
    this.urlParams = urlParams;
    this.initialized = true;
  }

  all() {
    return this.urlParams;
  }

  get<K extends keyof AppParameters>(key: K) {
    if (!this.initialized) {
      /**
       * For a long time we've seen thousands of these errors in Sentry,
       * but have failed to address the underlying issue. To reduce noise in Sentry
       * we should stop sending these logs until we believe that we've fixed the issue:
       * That views are rendered and accessing applicationParameters before the critical data
       * have been loaded and parsed.
       */
    }

    return this.urlParams[key];
  }
}

export const applicationParameters = new ApplicationParameters();
